import FetchClient, { type ApiResponse } from "./FetchClient";
import { addLogfrogPaymentMetadata } from "../utils/logfrog";

function withLogfrogPaymentMetadata(endpoint: string): string {
  const [path, query = ""] = endpoint.split("?", 2);
  const params = addLogfrogPaymentMetadata(new URLSearchParams(query));
  const encoded = params.toString();
  return encoded ? `${path}?${encoded}` : path;
}

export const getProductLink = async (
  priceIds: string[],
  sourceContext?: "billing_tab"
) => {
  console.log("getProductLink called with:", { priceIds, sourceContext });
  const params = new URLSearchParams();
  params.set("priceIds", priceIds.join(","));
  if (sourceContext) {
    params.set("sourceContext", sourceContext);
  }
  addLogfrogPaymentMetadata(params);
  const url = `/product-link?${params.toString()}`;
  console.log("Making request to:", url);

  const response = await FetchClient.get<
    ApiResponse<{ link: string; allUrls?: string; reused?: boolean }>
  >(url);
  console.log("Product link response:", response.data);
  return response.data;
};

export const getMultiProductLink = async (
  priceIds: string[]
) => {
  console.log("getMultiProductLink called with:", { priceIds });
  const url = withLogfrogPaymentMetadata(`/multi-product-link?priceIds=${priceIds.join(",")}`);
  console.log("Making request to:", url);

  const response = await FetchClient.get<ApiResponse<{ link: string; reused?: boolean }>>(url);
  console.log("Multi product link response:", response.data);
  return response.data;
};

export const getMixedProductLink = async (
  priceIds: string[],
  includeHomeDepot: boolean
) => {
  console.log("getMixedProductLink called with:", {
    priceIds,
    includeHomeDepot,
  });
  const params = new URLSearchParams();
  params.set("priceIds", priceIds.join(","));
  if (includeHomeDepot) {
    params.set("includeHomeDepot", "true");
  }

  addLogfrogPaymentMetadata(params);
  const url = `/mixed-product-link?${params.toString()}`;
  console.log("Making request to:", url);

  const response = await FetchClient.get<ApiResponse<{ link: string; reused?: boolean }>>(url);
  console.log("Mixed product link response:", response.data);
  return response.data;
};

export async function getManageBillingLink() {
  const response = await FetchClient.get<ApiResponse<{ link: string }>>(
    "/manage-billing"
  );
  return response.data;
}

export async function cancelSubscription(reason?: string, description?: string) {
  const response = await FetchClient.post<ApiResponse<{ message: string }>>(
    "/cancel-subscription",
    { reason, description }
  );
  return response.data;
}

export async function cancelRetailerSubscription(retailer: string) {
  const response = await FetchClient.post<ApiResponse<{ message: string }>>(
    "/cancel-subscription",
    { retailer }
  );
  return response.data;
}

export interface PauseSubscriptionResponse {
  /** When billing resumes (ms). */
  pausedUntil: number;
  /** End of the already-paid period; full access continues until then (ms). */
  accessUntil?: number;
  subscriptionId: string;
}

export async function pauseSubscription(months: 1 | 2 | 3, reason?: string) {
  const body: { months: 1 | 2 | 3; reason?: string } = { months };
  if (reason) body.reason = reason;
  const response = await FetchClient.post<ApiResponse<PauseSubscriptionResponse>>(
    "/subscription/pause",
    body
  );
  return response.data;
}

export async function resumeSubscription() {
  const response = await FetchClient.post<ApiResponse<{ resumed: boolean }>>(
    "/subscription/resume",
    {}
  );
  return response.data;
}


export async function updateSubscription(priceIds: string[]) {
  const response = await FetchClient.post<
    ApiResponse<{
      message: string;
      subscriptionId: string;
      retailers: string[];
    }>
  >("/update-subscription", { priceIds });
  return response.data;
}

export interface BillingPreviewItem {
  priceId: string;
  retailer: string;
  plan: string;
  amount: number;
  currency: string;
}

export interface BillingPreviewFinancialImpact {
  addingTotal: number;
  removingTotal: number;
  newMonthlyTotal: number;
  prorationCredit: number;
  prorationCharge: number;
  netProrationImpact: number;
  currency: string;
}

export interface BillingPreviewResponse {
  adding: BillingPreviewItem[] | null;
  removing: BillingPreviewItem[] | null;
  keeping: BillingPreviewItem[] | null;
  financialImpact: BillingPreviewFinancialImpact;
}

export async function previewBillingChanges(priceIds: string[]) {
  const response = await FetchClient.post<ApiResponse<BillingPreviewResponse>>(
    "/preview-billing-changes",
    { priceIds }
  );
  return response.data;
}

export const getProductLinkUpdate = async (
  priceIds: string[]
) => {
  console.log("getProductLinkUpdate called with:", { priceIds });

  // Use updateSubscription instead of creating checkout sessions
  const response = await updateSubscription(priceIds);
  console.log("Subscription update response:", response);

  return {
    success: true,
    message: response.message,
    subscriptionId: response.subscriptionId,
    retailers: response.retailers,
  };
};


export interface PastDueSubscriptionResponse {
  hasPastDueSubscription: boolean;
  billingPortalLink: string | null;
  subscriptionId?: string;
  subscriptionStatus?: string;
  needsReinstatement?: boolean;
  hadPreviousSubscription?: boolean;
  lastSubscriptionId?: string;
}

export async function checkPastDueSubscription(): Promise<PastDueSubscriptionResponse> {
  const response = await FetchClient.get<
    ApiResponse<PastDueSubscriptionResponse>
  >("/check-past-due-subscription");
  return response.data;
}

export interface ScheduledCancellationResponse {
  hasScheduledCancellation: boolean;
  cancelAtPeriodEnd: boolean;
  currentPeriodEnd: number | null;
  daysRemaining: number;
  subscriptionId?: string;
  subscriptionStatus?: string;
}

export async function checkScheduledCancellation(): Promise<ScheduledCancellationResponse> {
  const response = await FetchClient.get<
    ApiResponse<ScheduledCancellationResponse>
  >("/check-scheduled-cancellation");
  return response.data;
}

export async function reactivateSubscription() {
  const response = await FetchClient.post<ApiResponse<{ message: string }>>(
    "/reactivate-subscription",
    {}
  );
  return response.data;
}

export interface CreatePaymentIntentResponse {
  clientSecret: string;
  paymentIntentId: string;
}

export async function createPaymentIntent(
  priceIds: string[]
): Promise<CreatePaymentIntentResponse> {
  const response = await FetchClient.post<
    ApiResponse<CreatePaymentIntentResponse>
  >(withLogfrogPaymentMetadata("/create-payment-intent"), { priceIds });
  return response.data;
}

export interface ConfirmPaymentResponse {
  message: string;
  subscriptionId: string;
  retailers: string[];
  status: string;
}

export async function confirmPayment(
  paymentIntentId: string
): Promise<ConfirmPaymentResponse> {
  const response = await FetchClient.post<
    ApiResponse<ConfirmPaymentResponse>
  >("/confirm-payment", { paymentIntentId });
  return response.data;
}

// New paywall subscription management types
export interface SubscriptionStatusResponse {
  hasSubscription: boolean;
  isLegacyPlan: boolean;
  currentPlan: "none" | "basic" | "plus" | "pro" | "legacy";
  retailers: string[];
  priceId: string;
  canUpgrade: boolean;
  canDowngrade: boolean;
  monthlyAmount: number;
	nextChargeAmount?: number;
	currency?: string;
	billingInterval?: "day" | "week" | "month" | "year" | string;
	autoRenew?: boolean;
  periodEnd: number;
  hasActiveDiscount?: boolean;
  discountPercent?: number;
}

export interface SwitchPlanResponse {
  action: "redirect" | "scheduled";
  checkoutUrl?: string;
  message: string;
  periodEnd?: number;
}

export interface MigratePlanResponse {
  checkoutUrl: string;
  message: string;
}

// Get current subscription status
export async function getSubscriptionStatus(): Promise<SubscriptionStatusResponse> {
  const response = await FetchClient.get<ApiResponse<SubscriptionStatusResponse>>(
    "/subscription-status"
  );
  return response.data;
}

// Switch between Basic and Pro plans (upgrade requires payment, downgrade scheduled)
// Pass { source: "cancel_flow", reason } only when the switch is accepted as a
// save offer in the cancel flow; the server records it in the churn ledger.
export async function switchPlan(
  targetPlan: "basic" | "plus" | "pro",
  options?: { source?: "cancel_flow"; reason?: string }
): Promise<SwitchPlanResponse> {
  const response = await FetchClient.post<ApiResponse<SwitchPlanResponse>>(
    withLogfrogPaymentMetadata("/switch-plan"),
    { targetPlan, ...(options?.source ? { source: options.source, reason: options.reason || "" } : {}) }
  );
  return response.data;
}

// Migrate from legacy plan to new Basic or Pro plan
export async function migratePlan(targetPlan: "basic" | "plus" | "pro"): Promise<MigratePlanResponse> {
  const response = await FetchClient.post<ApiResponse<MigratePlanResponse>>(
    withLogfrogPaymentMetadata("/migrate-plan"),
    { targetPlan }
  );
  return response.data;
}

// Retention discount types and functions
export interface RetentionStatusResponse {
  discountsUsed: number;
  discountsRemaining: number;
  maxDiscounts: number;
}

export async function getRetentionStatus(): Promise<RetentionStatusResponse> {
  const response = await FetchClient.get<ApiResponse<RetentionStatusResponse>>("/retention-status");
  return response.data;
}

export interface ApplyRetentionDiscountResponse {
  message: string;
  discountsUsed: number;
  discountsRemaining: number;
  nextBillingDate: number;
}

export async function applyRetentionDiscount(): Promise<ApplyRetentionDiscountResponse> {
  const response = await FetchClient.post<ApiResponse<ApplyRetentionDiscountResponse>>(
    "/apply-retention-discount",
    {}
  );
  return response.data;
}
